<script setup lang="ts">
import { EButton, EDropdownMenu, ETooltip, useTheme, type MenuItem } from '@erpx/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import AppIcon from '@/components/AppIcon.vue'

/**
 * Light / dark / system. Following the OS is the default because an ERP is open
 * all day and a screen that fights the rest of the desktop at 19:00 is a real
 * complaint, not a preference.
 */

const { theme, resolved, setTheme } = useTheme()
const { t } = useI18n()

const CHOICES = ['light', 'dark', 'system'] as const

const items = computed<MenuItem[]>(() =>
  CHOICES.map((choice) => ({
    id: choice,
    label: t(`shell.theme.${choice}`),
    ...(theme.value === choice ? { shortcut: t('shell.theme.current') } : {}),
  })),
)

function onSelect(item: MenuItem): void {
  if (item.id === 'light' || item.id === 'dark' || item.id === 'system') setTheme(item.id)
}
</script>

<template>
  <EDropdownMenu :items="items" :label="$t('shell.theme.menuLabel')" @select="onSelect">
    <template #trigger>
      <span>
        <ETooltip :content="$t('shell.theme.tooltip', { choice: $t(`shell.theme.${theme}`) })">
          <EButton variant="ghost" size="sm" icon-only :aria-label="$t('shell.theme.trigger')">
            <template #icon>
              <AppIcon :name="resolved === 'dark' ? 'moon' : 'sun'" :size="15" />
            </template>
          </EButton>
        </ETooltip>
      </span>
    </template>
  </EDropdownMenu>
</template>
